<template>
  <div class="slicer">
    <!-- toolbar: what is loaded, how far along, and the two saves -->
    <div class="slicer-bar">
      <div style="min-width:0;">
        <div class="truncate" style="font-weight:600;">{{ sourceName || 'No design loaded' }}</div>
        <div v-if="imageSrc" class="mono" style="color:var(--muted); font-size:13px;">
          {{ realWidth }} × {{ realHeight }}, slicing as {{ detectedSize }}
        </div>
      </div>
      <div class="flex items-center gap-2" style="flex-wrap:wrap;">
        <span class="mono" style="color:var(--muted);">
          {{ bands.length }} {{ bands.length === 1 ? 'band' : 'bands' }}, {{ totalCells }} {{ totalCells === 1 ? 'cell' : 'cells' }}
        </span>
        <AppButton @click="saveCuts">Save cuts</AppButton>
        <AppButton variant="primary" :disabled="exporting || !imageSrc" @click="sliceAndExport">
          {{ exporting ? (ocrStatus || 'Exporting…') : 'Slice and export' }}
        </AppButton>
      </div>
    </div>

    <div class="slicer-cols">
      <!-- left: controls -->
      <aside class="slicer-side">
        <section class="slicer-card">
          <h2 class="slicer-h">Source</h2>

          <!-- design comps already attached to this email (pushed in by the portal) -->
          <div v-if="designAssets.length" class="flex flex-col gap-1.5">
            <button
              v-for="asset in designAssets"
              :key="asset.role"
              type="button"
              class="asset-btn"
              :class="{ on: sourceRole === asset.role }"
              :disabled="loadingRole !== null"
              :title="`Slice the attached ${asset.name}`"
              @click="useAsset(asset)"
            >
              <UIcon v-if="asset.role === 'design_mobile'" name="i-heroicons-device-phone-mobile" class="h-4 w-4" />
              <UIcon v-else name="i-heroicons-computer-desktop" class="h-4 w-4" />
              <span class="truncate" style="flex:1; text-align:left;">{{ asset.name }}</span>
              <UIcon v-if="loadingRole === asset.role" name="i-heroicons-arrow-path" class="h-4 w-4 animate-spin" />
              <UIcon v-else-if="sourceRole === asset.role" name="i-heroicons-check" class="h-4 w-4" style="color:var(--good);" />
            </button>
          </div>

          <label class="dropzone">
            <UIcon name="i-heroicons-arrow-up-tray" class="h-5 w-5" />
            <span>{{ designAssets.length ? 'Or drop another design image' : 'Drop the full design image, or click to choose' }}</span>
            <span v-if="sourceName && !sourceRole" class="mono truncate" style="max-width:100%; color:var(--good); font-size:13px;">{{ sourceName }}</span>
            <input type="file" accept="image/*" style="display:none;" @change="handleFileSelect">
          </label>

          <!-- size comes from the asset's role when picked from the list, else from the filename -->
          <div v-if="sourceName" style="color:var(--muted); font-size:13px;">
            Slicing as {{ detectedSize }}, from the {{ sourceRole ? 'attached design' : 'file name' }}.
          </div>
        </section>

        <!-- after a desktop slice, prompt for the mobile design -->
        <section
          v-if="showMobilePrompt"
          class="slicer-card"
          style="border-color:color-mix(in srgb, var(--cyan) 30%, transparent); background:color-mix(in srgb, var(--cyan) 8%, var(--panel));"
        >
          <div style="color:var(--cyan); font-weight:600;">Desktop is sliced</div>
          <div style="color:var(--muted);">
            Have a mobile design too? Drop it here to slice it as well. Its file name needs to contain “mobile”.
          </div>
          <AppButton small style="align-self:flex-start;" @click="showMobilePrompt = false">Dismiss</AppButton>
        </section>

        <section class="slicer-card">
          <h2 class="slicer-h">Cut direction</h2>
          <ConsoleSegmentedToggle
            :model-value="cutMode"
            :options="[{ label: 'Horizontal', value: 'horizontal' }, { label: 'Vertical', value: 'vertical' }]"
            style="align-self:flex-start;"
            @update:model-value="(mode) => (cutMode = mode as 'horizontal' | 'vertical')"
          />
          <div style="color:var(--muted); font-size:13px;">
            Click the design to drop a cut{{ canDragEdit ? ', and drag a cut to move it' : '' }}. Vertical cuts split the selected band into cells.
          </div>
        </section>

        <!-- auto-slice (plan-gated): one-click full slice, and/or incremental suggestions -->
        <section v-if="canAutoSlice || canAutoSuggest" class="slicer-card">
          <h2 class="slicer-h">Auto-slice</h2>
          <!-- The stored value is a minimum confidence, so the slider runs the other way:
               further right means a lower bar and more cuts. -->
          <label
            class="flex flex-col gap-1"
            title="Auto-slice places every cut it is at least this sure about. Suggestions below that stay hidden."
          >
            <span class="flex items-center justify-between gap-2">
              <span>How many cuts</span>
              <span class="mono" style="color:var(--muted); font-size:13px;">{{ Math.round(minConfidence * 100) }}% sure or more</span>
            </span>
            <input
              :value="1 - minConfidence"
              type="range"
              min="0"
              max="1"
              step="0.05"
              aria-label="How many cuts auto-slice places"
              style="width:100%; accent-color:var(--accent);"
              @input="minConfidence = Math.round((1 - Number(($event.target as HTMLInputElement).value)) * 20) / 20"
              @change="saveMinConfidence"
            >
            <span class="flex justify-between" style="color:var(--muted); font-size:13px;"><span>Fewer</span><span>More</span></span>
          </label>
          <AppButton
            v-if="canAutoSlice"
            variant="primary"
            :disabled="!imageSrc || exporting || autoBusy"
            title="Place every horizontal and vertical cut automatically. It uses no AI: it learned from emails sliced before. Review and adjust, then save."
            @click="runAutoSlice"
          >Auto-slice</AppButton>
          <AppButton
            v-if="canAutoSuggest"
            :disabled="!imageSrc || exporting || autoBusy"
            title="Find likely cut lines in the image. Suggestions appear as amber dashed lines. Click one to accept it, or accept all."
            @click="runAutoSuggest"
          >Suggest cuts</AppButton>
          <div v-if="suggestionCount" class="flex flex-col gap-1.5">
            <span class="mono" style="color:var(--acctext); font-weight:600;">
              {{ shownSuggestions.length + shownSuggestionsV.length }} of {{ suggestionCount }} suggested
            </span>
            <div class="flex gap-1.5">
              <AppButton small style="flex:1;" :disabled="!shownSuggestions.length && !shownSuggestionsV.length" @click="acceptAllSuggestions">Accept all</AppButton>
              <AppButton small style="flex:1;" @click="dismissSuggestions">Dismiss</AppButton>
            </div>
          </div>
        </section>

        <section class="slicer-card">
          <label
            class="flex items-center gap-2"
            style="cursor:pointer; user-select:none;"
            title="Reads the words in each slice (OCR, tesseract.js) to fill in alt text for screen readers and image-blocked inboxes."
          >
            <input v-model="ocrEnabled" type="checkbox" style="width:16px; height:16px; accent-color:var(--accent); cursor:pointer;">
            Write alt text from the image
          </label>
          <div v-if="ocrStatus" class="mono" style="color:var(--muted); font-size:13px;">{{ ocrStatus }}</div>
          <AppButton small variant="danger" style="align-self:flex-start;" @click="resetCuts">Reset all cuts</AppButton>
        </section>

        <section class="slicer-keys">
          <h2 class="slicer-h">Shortcuts</h2>
          <div><kbd>⌫</kbd> Delete the selected cut</div>
          <div><kbd>↑</kbd><kbd>↓</kbd><kbd>←</kbd><kbd>→</kbd> Nudge the selected cut</div>
          <div v-if="canDragEdit"><kbd>Click</kbd> Select, <kbd>Drag</kbd> move</div>
          <div><kbd>Esc</kbd> Deselect</div>
          <div><kbd>V</kbd> Switch cut direction</div>
          <div><kbd>⇧</kbd> Hold to flip direction</div>
        </section>
      </aside>

      <!-- center: the design -->
      <div class="slicer-canvas">
        <div v-if="!imageSrc" style="color:var(--muted); margin-top:40px; text-align:center;">
          {{ designAssets.length ? 'Pick an attached design, or drop a design image, to begin slicing.' : 'Drop a design image to begin slicing.' }}
        </div>
        <div v-else class="slicer-stage">
          <!-- Band labels sit beside the artwork, so they never print over it. -->
          <div class="slicer-gutter">
            <template v-if="realHeight">
              <button
                v-for="band in bands"
                :key="band.index"
                type="button"
                class="gutter-label mono"
                :class="{ on: band.index === activeBand }"
                :style="{ top: (band.y0 / realHeight * 100) + '%' }"
                :aria-label="`Select band ${band.index + 1}`"
                @click="activeBand = band.index"
              ><b>b{{ band.index + 1 }}</b> {{ Math.round(band.y0) }}</button>
            </template>
          </div>
          <figure ref="figure" :style="{ position: 'relative', display: 'block', flex: '1 1 0', minWidth: 0, margin: 0, cursor: imageCursor }">
            <img
              ref="image"
              :src="imageSrc as string"
              alt=""
              style="display:block; width:100%; height:auto;"
              draggable="false"
              @pointerdown.prevent="onImageDown"
              @pointermove="onImageMove"
              @pointerup="onImageUp"
              @pointercancel="onImageUp"
              @pointerleave="onImageLeave"
            >
            <canvas ref="canvas" style="position:absolute; inset:0; width:100%; height:100%; pointer-events:none;" />
          </figure>
        </div>
      </div>

      <!-- right: bands and their cells -->
      <aside class="slicer-side">
        <div class="flex items-baseline justify-between gap-2">
          <h2 class="display" style="margin:0; font-size:20px; font-weight:700;">Bands</h2>
          <span v-if="bands.length" style="color:var(--muted); font-size:13px;">Click a band to select it</span>
        </div>

        <div v-if="!bands.length" style="color:var(--muted);">No cuts yet.</div>

        <div v-else class="flex flex-col gap-2">
          <div
            v-for="band in bands"
            :key="band.index"
            class="band-card"
            :class="{ on: band.index === activeBand }"
            @click="activeBand = band.index"
          >
            <button type="button" class="band-head mono">
              <b>b{{ band.index + 1 }}</b>
              <span style="color:var(--muted); font-size:13px;">y {{ Math.round(band.y0) }}</span>
              <span style="flex:1;" />
              <span style="color:var(--muted); font-size:13px;">{{ band.cells.length }} {{ band.cells.length === 1 ? 'cell' : 'cells' }}</span>
            </button>
            <div v-for="(cell, cellIndex) in band.cells" :key="cellIndex" class="band-cell">
              <span class="mono truncate" style="width:74px; flex-shrink:0; color:var(--muted); font-size:13px;">{{ cellFilename(band, cellIndex) }}</span>
              <input
                :value="links[`${band.index}-${cellIndex}`] || ''"
                type="url"
                :aria-label="`Link for ${cellFilename(band, cellIndex)}`"
                placeholder="No link yet"
                class="console-input"
                style="height:32px; font-size:13px;"
                @input="setLink(band.index, cellIndex, ($event.target as HTMLInputElement).value)"
              >
            </div>
          </div>
        </div>
      </aside>
    </div>

    <!-- Blocking overlay while slicing/exporting: covers the whole viewport so the user
         can't change cuts, navigate, or re-trigger the export mid-run. -->
    <Teleport to="body">
      <div
        v-if="exporting"
        class="slicer-overlay"
        role="alertdialog"
        aria-busy="true"
        aria-live="assertive"
        aria-label="Slicing and exporting"
        @click.stop
        @keydown.stop
      >
        <div class="slicer-overlay__card">
          <div class="slicer-spinner" />
          <div class="slicer-overlay__status">{{ ocrStatus || 'Exporting…' }}</div>
          <div class="slicer-overlay__bar">
            <div
              class="slicer-overlay__bar-fill"
              :class="{ 'is-indeterminate': !exportProgress }"
              :style="exportProgress ? { width: exportProgress.pct + '%' } : undefined"
            />
          </div>
          <div class="slicer-overlay__hint">Please don’t close or leave this page.</div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from "vue"
import { useEmails } from "~/composables/useEmails"
import { normalizeSections, serializeSections } from "~/composables/useEmailSections"
import type { CutSuggestion, VCutSuggestion } from "~/composables/useAutoSlice"

const route = useRoute()
const { groupAlias, emailAlias } = route.params as { groupAlias: string, emailAlias: string }
const { emailsStore } = await useEmails()

const email = computed(() => emailsStore.map?.[emailAlias] || null)

const figure = ref<HTMLElement | null>(null)
const image = ref<HTMLImageElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
const imageSrc = ref<string | ArrayBuffer | null>(null)
const sourceName = ref('')
// Set only when the source came from an attached design asset — its role names the size
// outright, so we skip guessing from the filename.
const sourceRole = ref<'design_desktop' | 'design_mobile' | null>(null)
const loadingRole = ref<string | null>(null)
// Object URL for a proxied asset; revoked when the source is replaced so blobs don't leak.
const objectUrl = ref<string | null>(null)
// Size comes from the asset role when available, else from the uploaded filename; surfaced
// so the user can confirm, and used to prompt for the mobile design after a desktop slice.
const detectedSize = computed(() => prefixFromName())
const showMobilePrompt = ref(false)

const designAssets = computed<EmailAssetT[]>(() =>
  (email.value?.assets || []).filter(a => a.role === 'design_desktop' || a.role === 'design_mobile'),
)

const realWidth = ref(0)
const realHeight = ref(0)

const cutMode = ref<'horizontal' | 'vertical'>('horizontal')
const hCuts = ref<number[]>([]) // Y positions (exclusive of 0 / height)
const vCuts = ref<number[][]>([]) // per-band X positions
const activeBand = ref(0)
const links = ref<Record<string, string>>({})
const exporting = ref(false)

// ---- Plan-gated editing modules ----
const features = useFeatures()
const canDragEdit = computed(() => features.has('slicer.dragEdit'))       // select + drag cuts
const canAutoSuggest = computed(() => features.has('slicer.autoSuggest')) // ML/CV suggestions
const canAutoSlice = computed(() => features.has('slicer.autoSlice'))     // one-click full slice

// Selection: which cut is active for keyboard nudge / delete / drag. A horizontal cut is
// (kind:'h', idx into sortedH); a vertical cut is (kind:'v', band, idx into that band's vCuts).
const selected = ref<{ kind: 'h'; idx: number } | { kind: 'v'; band: number; idx: number } | null>(null)
const dragging = ref(false)
type CutRef = { kind: 'h'; idx: number } | { kind: 'v'; band: number; idx: number }
// The cut under the pointer (drag-edit): it is highlighted and the cursor shows which way it moves.
const hoverCut = ref<CutRef | null>(null)
const imageCursor = computed(() => {
  const c = dragging.value ? selected.value : hoverCut.value
  return c ? (c.kind === 'h' ? 'ns-resize' : 'ew-resize') : 'crosshair'
})
// How far the dragged cut may travel: up to its neighbours, so bands keep their order and
// each band's vertical cuts stay with it.
let dragBounds = { min: 0, max: 0 }

// Auto-slice suggestions (candidates not yet accepted), plus the detector.
type Suggestion = CutSuggestion
const suggested = ref<Suggestion[]>([])
// One slider for both flows: auto-slice places cuts at or above it, and only suggestions at or
// above it are drawn, clickable and taken by "accept all". It starts at the cutoff the shipped
// model was trained to (asked from the server, which holds the model) and is remembered per browser.
const MIN_CONFIDENCE_KEY = 'slicer.minConfidence'
const minConfidence = ref(0.6)
onMounted(async () => {
  const saved = Number(localStorage.getItem(MIN_CONFIDENCE_KEY))
  if (saved > 0 && saved <= 1) { minConfidence.value = saved; return }
  if (!canAutoSlice.value && !canAutoSuggest.value) return
  try {
    minConfidence.value = (await $fetch<{ threshold: number }>('/api/v1/slicer/auto')).threshold
  } catch { /* keep the fallback */ }
})
// saved only when the user moves it, so an untouched slider follows the model's cutoff
function saveMinConfidence() {
  try { localStorage.setItem(MIN_CONFIDENCE_KEY, String(minConfidence.value)) } catch { /* private mode */ }
}
const shownSuggestions = computed(() => suggested.value.filter((s) => s.confidence >= minConfidence.value))
// Vertical suggestions: x within the rows y0..y1, which may not line up with a band yet.
const suggestedV = ref<VCutSuggestion[]>([])
const shownSuggestionsV = computed(() => suggestedV.value.filter((s) => s.confidence >= minConfidence.value))
const suggestionCount = computed(() => suggested.value.length + suggestedV.value.length)
watch(minConfidence, () => render())
const { suggestCuts, autoSliceImage } = useAutoSlice()
const autoBusy = ref(false) // a request to the auto-slice endpoint is in flight

// OCR auto alt-text (tesseract.js). Enabled by default; produces alt text per slice.
const ocrEnabled = ref(true)
const ocrStatus = ref('')
const { recognize: ocrRecognize, terminate: ocrTerminate } = useOcr()

// Parse "reading text… 12/15" into a fraction so the blocking overlay can show a bar.
const exportProgress = computed(() => {
  const m = /(\d+)\s*\/\s*(\d+)/.exec(ocrStatus.value)
  if (!m) return null
  const done = Number(m[1])
  const total = Number(m[2]) || 1
  return { done, total, pct: Math.round((done / total) * 100) }
})

// Hover guide + orientation modifier (hold Shift to temporarily flip cut orientation).
const hover = ref<{ x: number; y: number } | null>(null)
const flipHeld = ref(false)
const effectiveMode = computed<'horizontal' | 'vertical'>(() =>
  flipHeld.value ? (cutMode.value === 'horizontal' ? 'vertical' : 'horizontal') : cutMode.value,
)

const sortedH = computed(() => [...hCuts.value].sort((a, b) => a - b))

type Band = { index: number; y0: number; y1: number; cells: Array<{ x0: number; x1: number }> }

const bands = computed<Band[]>(() => {
  if (!realHeight.value) return []
  const ys = [0, ...sortedH.value, realHeight.value]
  const out: Band[] = []
  for (let i = 0; i < ys.length - 1; i++) {
    const vx = [...(vCuts.value[i] || [])].sort((a, b) => a - b)
    const xs = [0, ...vx, realWidth.value]
    const cells: Array<{ x0: number; x1: number }> = []
    for (let j = 0; j < xs.length - 1; j++) cells.push({ x0: xs[j]!, x1: xs[j + 1]! })
    out.push({ index: i, y0: ys[i]!, y1: ys[i + 1]!, cells })
  }
  return out
})

const totalCells = computed(() => bands.value.reduce((sum, b) => sum + b.cells.length, 0))

// Reflect counts in the sub-bar status.
const subbarStatus = useSubbarStatus()
watch([bands, totalCells], () => {
  if (imageSrc.value) {
    subbarStatus.value = { text: `${bands.value.length} bands · ${totalCells.value} cells`, color: 'cyan' }
  }
})



function prefixFromName(): string {
  if (sourceRole.value) return sourceRole.value === 'design_mobile' ? 'mobile' : 'desktop'
  const base = sourceName.value.split('.')[0] || 'slice'
  if (/mobile/i.test(base)) return 'mobile'
  if (/desktop/i.test(base)) return 'desktop'
  return 'desktop'
}


function cellLetter(band: Band, cellIndex: number): string {
  return band.cells.length > 1 ? String.fromCharCode(97 + cellIndex) : ''
}

// Filename/src suffix for a cell: single-cell bands get no suffix ("$SIZE_04"), split
// bands get an underscore-separated cell letter ("$SIZE_04_a", "$SIZE_04_b").
function cellSuffix(band: Band, cellIndex: number): string {
  const l = cellLetter(band, cellIndex)
  return l ? `_${l}` : ''
}

function cellFilename(band: Band, cellIndex: number): string {
  const num = String(band.index + 1).padStart(2, '0')
  return `${num}${cellSuffix(band, cellIndex)}.jpg`
}

function setLink(bandIndex: number, cellIndex: number, value: string) {
  links.value[`${bandIndex}-${cellIndex}`] = value
}

// ---- File handling ----
function releaseObjectUrl() {
  if (objectUrl.value) {
    URL.revokeObjectURL(objectUrl.value)
    objectUrl.value = null
  }
}

function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  releaseObjectUrl()
  sourceName.value = file.name
  sourceRole.value = null // size falls back to filename detection for hand-picked files
  showMobilePrompt.value = false // fresh upload — clear any prior "slice mobile" prompt
  const reader = new FileReader()
  reader.onload = (e) => { imageSrc.value = e.target?.result ?? null }
  reader.readAsDataURL(file)
}

// Load an attached design comp as the slice source. The bytes come through our own origin
// (see server/api/v1/emails/[groupAlias]/[emailAlias]/asset.get.ts) — loading the R2 URL
// directly into the <img> would taint the export canvas and break toBlob().
async function useAsset(asset: EmailAssetT) {
  if (loadingRole.value) return
  loadingRole.value = asset.role
  try {
    const blob = await $fetch<Blob>(`/api/v1/emails/${groupAlias}/${emailAlias}/asset`, {
      query: { role: asset.role },
      responseType: 'blob',
    })
    releaseObjectUrl()
    objectUrl.value = URL.createObjectURL(blob)
    sourceName.value = asset.name
    sourceRole.value = asset.role as 'design_desktop' | 'design_mobile'
    showMobilePrompt.value = false
    imageSrc.value = objectUrl.value
  } catch (error) {
    useToast().add({
      title: 'Could not load asset',
      description: (error as { statusMessage?: string })?.statusMessage || `Failed to load "${asset.name}".`,
      color: 'error',
    })
  } finally {
    loadingRole.value = null
  }
}

watch(imageSrc, async () => {
  selected.value = null
  suggested.value = []; suggestedV.value = []
  if (!imageSrc.value) return
  const probe = new Image()
  probe.onload = async () => {
    realWidth.value = probe.width
    realHeight.value = probe.height
    // The <canvas> is inside a v-else that only renders once imageSrc is set,
    // so wait for the DOM before sizing/drawing it.
    await nextTick()
    if (canvas.value) {
      canvas.value.width = probe.width
      canvas.value.height = probe.height
    }
    render()
  }
  probe.src = imageSrc.value as string
})

// ---- Drawing ----
// Acquire the 2D context per-call: the canvas mounts only after an image is dropped,
// so a context grabbed in onMounted would be null. getContext() is idempotent.
function render() {
  const c = canvas.value
  if (!c) return
  const g = c.getContext('2d')
  if (!g) return
  g.clearRect(0, 0, c.width, c.height)

  // The canvas backing store is at natural resolution but displayed scaled down, so strokes
  // sized in natural px render sub-pixel and vanish. k = natural px per on-screen px keeps
  // every overlay a constant, visible thickness regardless of image size.
  const dispW = image.value ? image.value.getBoundingClientRect().width : realWidth.value
  const k = dispW > 0 ? realWidth.value / dispW : 4.5

  // highlight active band
  const active = bands.value[activeBand.value]
  if (active) {
    g.fillStyle = 'rgba(255,178,36,0.12)'
    g.fillRect(0, active.y0, realWidth.value, active.y1 - active.y0)
  }

  // auto-slice suggestions (amber dashed ghost lines) — click one to accept
  if (shownSuggestions.value.length || shownSuggestionsV.value.length) {
    g.lineWidth = 2 * k
    g.setLineDash([14 * k, 10 * k])
    g.font = `${11 * k}px ui-monospace, monospace`
    g.textAlign = 'right'
    for (const sug of shownSuggestions.value) {
      // higher ranker confidence → more opaque (0.5 = no ranker loaded → uniform)
      g.strokeStyle = g.fillStyle = `rgba(255,178,36,${(0.55 + sug.confidence * 0.45).toFixed(2)})`
      g.beginPath(); g.moveTo(0, sug.y); g.lineTo(realWidth.value, sug.y); g.stroke()
      g.fillText(`${Math.round(sug.confidence * 100)}%`, realWidth.value - 6 * k, sug.y - 4 * k)
    }
    for (const sug of shownSuggestionsV.value) {
      g.strokeStyle = `rgba(255,178,36,${(0.55 + sug.confidence * 0.45).toFixed(2)})`
      g.beginPath(); g.moveTo(sug.x, sug.y0); g.lineTo(sug.x, sug.y1); g.stroke()
    }
    g.setLineDash([])
    g.textAlign = 'left'
  }

  // horizontal cut lines (magenta); the selected cut is cream and thicker, with a handle
  const selY = selected.value?.kind === 'h' ? hCuts.value[selected.value.idx] : null
  const hovY = hoverCut.value?.kind === 'h' ? hCuts.value[hoverCut.value.idx] : null
  for (const y of sortedH.value) {
    const isSel = selY != null && Math.abs(y - selY) < 0.5
    const isHov = hovY != null && Math.abs(y - hovY) < 0.5
    const h = (isSel || isHov ? 4 : 2.5) * k
    g.fillStyle = isSel ? '#fff4d6' : isHov ? '#ff9cc8' : '#ff5fa8'
    g.fillRect(0, y - h / 2, realWidth.value, h)
    if (isSel || isHov) g.fillRect(realWidth.value / 2 - 14 * k, y - 7 * k, 28 * k, 14 * k) // grab handle
  }

  // Band labels are DOM elements in the gutter beside the image, not drawn here.

  // vertical cut lines for every band (magenta dashed). The active band's cuts are drawn
  // brighter/thicker; inactive bands stay dimmer so the active row still stands out but
  // no cuts ever visually disappear when switching rows.
  for (const band of bands.value) {
    const xs = vCuts.value[band.index] || []
    if (!xs.length) continue
    const isActive = band.index === activeBand.value
    const sel = selected.value?.kind === 'v' && selected.value.band === band.index ? selected.value.idx : -1
    const hov = hoverCut.value?.kind === 'v' && hoverCut.value.band === band.index ? hoverCut.value.idx : -1
    xs.forEach((x, i) => {
      const isSel = i === sel, isHov = i === hov
      g.strokeStyle = isSel ? '#fff4d6' : isHov || isActive ? '#ff5fa8' : 'rgba(255,95,168,0.5)'
      g.lineWidth = (isSel || isHov ? 3 : isActive ? 2 : 1.5) * k
      g.setLineDash(isSel || isHov ? [] : [8 * k, 6 * k])
      g.beginPath()
      g.moveTo(x, band.y0)
      g.lineTo(x, band.y1)
      g.stroke()
    })
  }
  g.setLineDash([])

  // hover guide — previews where a click will cut, in the effective orientation (pale blue).
  // Drawn as a soft dark under-stroke plus a bright glowing cyan line so it stays legible
  // over both light and busy image regions.
  if (hover.value && !hoverCut.value && !dragging.value) {
    let ax = 0, ay = 0, bx = 0, by = 0
    if (effectiveMode.value === 'horizontal') {
      ax = 0; ay = hover.value.y; bx = realWidth.value; by = hover.value.y
    } else {
      // vertical guide spans the band under the cursor (where the cut would land)
      const band = bands.value.find((b) => hover.value!.y >= b.y0 && hover.value!.y < b.y1)
      ax = hover.value.x; ay = band ? band.y0 : 0
      bx = hover.value.x; by = band ? band.y1 : realHeight.value
    }

    // dark backing stroke for contrast on light areas
    g.strokeStyle = 'rgba(0,0,0,0.35)'
    g.lineWidth = 5 * k
    g.setLineDash([])
    g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx, by); g.stroke()

    // bright glowing trace line
    g.strokeStyle = 'rgba(143,199,232,1)'
    g.lineWidth = 2.5 * k
    g.setLineDash([9 * k, 5 * k])
    g.shadowColor = 'rgba(143,199,232,0.9)'
    g.shadowBlur = 8 * k
    g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx, by); g.stroke()
    g.shadowBlur = 0
    g.setLineDash([])
  }
}

watch([bands, activeBand], () => nextTick(render))

// ---- Click to cut ----
function toNaturalCoords(event: MouseEvent): { x: number; y: number } | null {
  if (!realWidth.value || !image.value) return null
  const rect = image.value.getBoundingClientRect()
  return {
    x: Math.round((event.clientX - rect.left) / (rect.width / realWidth.value)),
    y: Math.round((event.clientY - rect.top) / (rect.height / realHeight.value)),
  }
}

// Natural-pixel hit radius for a constant ~12px on-screen tolerance (image is scaled down).
function hitRadius(): { y: number; x: number } {
  if (!image.value || !realWidth.value) return { y: 15, x: 15 }
  const r = image.value.getBoundingClientRect()
  return { y: 12 / (r.height / realHeight.value), x: 12 / (r.width / realWidth.value) }
}

// Vertical cuts are stored per band, by the band's position. So every change to the horizontal
// cuts goes through these three, which keep each band's vertical cuts with it.

/** Add a horizontal cut. It splits one band in two, and both halves keep that band's vertical cuts. */
function insertHCut(y: number) {
  const k = bands.value.findIndex((b) => y >= b.y0 && y < b.y1)
  hCuts.value.push(y)
  if (k < 0) return
  while (vCuts.value.length <= k) vCuts.value.push([])
  vCuts.value.splice(k + 1, 0, [...vCuts.value[k]!])
  if (activeBand.value > k) activeBand.value++
}

/** Remove a horizontal cut. The bands on either side merge and pool their vertical cuts. */
function removeHCut(idx: number) {
  const y = hCuts.value[idx]
  if (y === undefined) return
  const k = sortedH.value.indexOf(y) // the cut sits between bands k and k + 1
  hCuts.value.splice(idx, 1)
  while (vCuts.value.length <= k + 1) vCuts.value.push([])
  const pooled: number[] = []
  for (const x of [...vCuts.value[k]!, ...vCuts.value[k + 1]!]) if (!pooled.some((o) => Math.abs(o - x) < 4)) pooled.push(x)
  vCuts.value.splice(k, 2, pooled)
  if (activeBand.value > k) activeBand.value--
}

/** Move a horizontal cut, stopping short of its neighbours so the bands keep their order. */
function moveHCut(idx: number, y: number) {
  const at = hCuts.value[idx]
  if (at === undefined) return
  const lo = Math.max(0, ...hCuts.value.filter((c) => c < at)) + 4
  const hi = Math.min(realHeight.value, ...hCuts.value.filter((c) => c > at)) - 4
  hCuts.value[idx] = Math.max(lo, Math.min(hi, y))
}

// Add a new cut at the given natural coords (the classic click-to-cut behaviour). Selects it
// when drag-edit is on so it can be dragged/nudged immediately.
function addCutAt(x: number, y: number) {
  if (effectiveMode.value === 'horizontal') {
    const idx = hCuts.value.findIndex((c) => Math.abs(c - y) < 50)
    if (idx >= 0) { moveHCut(idx, y); selected.value = { kind: 'h', idx } }
    else { insertHCut(y); selected.value = { kind: 'h', idx: hCuts.value.length - 1 } }
  } else {
    const bandIndex = bands.value.findIndex((b) => y >= b.y0 && y < b.y1)
    if (bandIndex >= 0) {
      activeBand.value = bandIndex
      if (!vCuts.value[bandIndex]) vCuts.value[bandIndex] = []
      const arr = vCuts.value[bandIndex]!
      const idx = arr.findIndex((c) => Math.abs(c - x) < 40)
      if (idx >= 0) { arr[idx] = x; selected.value = { kind: 'v', band: bandIndex, idx } }
      else { arr.push(x); selected.value = { kind: 'v', band: bandIndex, idx: arr.length - 1 } }
    }
  }
  render()
}

// Mouse-down: accept a suggestion, or (drag-edit) select+grab a nearby cut, else add a cut.
function onImageDown(event: PointerEvent) {
  if (exporting.value) return
  const p = toNaturalCoords(event); if (!p) return
  const hit = hitRadius()
  hoverCut.value = null

  // 1) clicking on/near an auto-suggested ghost line accepts it
  if (canAutoSuggest.value && shownSuggestions.value.length) {
    const near = shownSuggestions.value.find((s) => Math.abs(s.y - p.y) < hit.y * 1.6)
    if (near) { acceptSuggestion(suggested.value.indexOf(near)); return }
  }
  if (canAutoSuggest.value) {
    const nearV = shownSuggestionsV.value.find((s) => Math.abs(s.x - p.x) < hit.x * 1.6 && p.y >= s.y0 && p.y < s.y1)
    if (nearV) { acceptSuggestionV(nearV); render(); return }
  }

  // 2) drag-edit: grab the cut under the pointer and keep receiving moves outside the image
  const cut = canDragEdit.value ? cutAt(p) : null
  if (cut) {
    if (cut.kind === 'v') activeBand.value = cut.band
    selected.value = cut
    dragging.value = true
    const gap = 4
    const all = cut.kind === 'h' ? hCuts.value : (vCuts.value[cut.band] || [])
    const at = all[cut.idx]!, limit = cut.kind === 'h' ? realHeight.value : realWidth.value
    dragBounds = {
      min: Math.max(0, ...all.filter((c) => c < at)) + gap,
      max: Math.min(limit, ...all.filter((c) => c > at)) - gap,
    }
    ;(event.target as Element).setPointerCapture?.(event.pointerId)
    render()
    return
  }

  // 3) empty space → add a new cut
  addCutAt(p.x, p.y)
}

// The existing cut within grabbing distance of a point, preferring whichever is closer on screen.
function cutAt(p: { x: number; y: number }): CutRef | null {
  const hit = hitRadius()
  let hi = -1, hd = hit.y
  hCuts.value.forEach((c, i) => { const d = Math.abs(c - p.y); if (d < hd) { hd = d; hi = i } })
  const band = bands.value.find((b) => p.y >= b.y0 && p.y < b.y1)
  let vi = -1, vd = hit.x
  if (band) (vCuts.value[band.index] || []).forEach((c, i) => { const d = Math.abs(c - p.x); if (d < vd) { vd = d; vi = i } })
  if (hi >= 0 && (vi < 0 || hd / hit.y <= vd / hit.x)) return { kind: 'h', idx: hi }
  return vi >= 0 && band ? { kind: 'v', band: band.index, idx: vi } : null
}

function onImageMove(event: PointerEvent) {
  const p = toNaturalCoords(event)
  if (dragging.value && selected.value && p) {
    const to = Math.max(dragBounds.min, Math.min(dragBounds.max, selected.value.kind === 'h' ? p.y : p.x))
    if (selected.value.kind === 'h') hCuts.value[selected.value.idx] = to
    else { const arr = vCuts.value[selected.value.band]; if (arr) arr[selected.value.idx] = to }
  }
  hoverCut.value = !dragging.value && p && canDragEdit.value && !exporting.value ? cutAt(p) : null
  hover.value = p
  render()
}

function onImageUp() {
  dragging.value = false
}

// A captured drag keeps going outside the image, so leaving only clears the hover state.
function onImageLeave() {
  if (dragging.value) return
  hover.value = null
  hoverCut.value = null
  render()
}

// ---- Auto-slice ----
// One click replaces all cuts with the model's horizontal + per-band vertical cuts. The
// previous cuts are kept for a one-step undo from the toast.
// The detector runs on the server; report why a request failed (plan, size, network).
function autoSliceFailed(e: unknown) {
  const err = e as { statusMessage?: string; data?: { statusMessage?: string } }
  useToast().add({
    title: 'Auto-slice failed',
    description: err?.data?.statusMessage || err?.statusMessage || 'Could not reach the auto-slice service.',
    color: 'error',
  })
}

async function runAutoSlice() {
  if (!image.value || !realHeight.value || autoBusy.value) return
  autoBusy.value = true
  let result: Awaited<ReturnType<typeof autoSliceImage>> = null
  try {
    result = await autoSliceImage(image.value, realWidth.value, realHeight.value, minConfidence.value)
  } catch (e) {
    autoSliceFailed(e)
  } finally {
    autoBusy.value = false
  }
  if (!result) return
  const previous = { hCuts: [...hCuts.value], vCuts: vCuts.value.map((v) => [...v]) }
  hCuts.value = result.hCuts
  vCuts.value = result.vCuts
  activeBand.value = 0
  selected.value = null
  suggested.value = []; suggestedV.value = []
  render()
  const cells = result.vCuts.reduce((n, v) => n + v.length, 0) + result.hCuts.length + 1
  useToast().add({
    title: 'Auto-sliced',
    description: `${result.hCuts.length + 1} bands · ${cells} cells. Review the cuts, adjust if needed, then save.`,
    color: 'success',
    actions: previous.hCuts.length || previous.vCuts.some((v) => v.length)
      ? [{
          label: 'Undo',
          onClick: () => {
            hCuts.value = previous.hCuts
            vCuts.value = previous.vCuts
            selected.value = null
            render()
          },
        }]
      : undefined,
  })
}

async function runAutoSuggest() {
  if (!image.value || !realHeight.value || autoBusy.value) return
  autoBusy.value = true
  try {
    const found = await suggestCuts(image.value, realWidth.value, realHeight.value, hCuts.value)
    suggested.value = found.h.filter((s) => !hCuts.value.some((h) => Math.abs(h - s.y) < 20)) // drop ones already cut
    suggestedV.value = found.v.filter((s) => !bands.value.some((b) => // drop ones already cut
      b.y0 < s.y1 && b.y1 > s.y0 && (vCuts.value[b.index] || []).some((x) => Math.abs(x - s.x) < 20)))
  } catch (e) {
    autoSliceFailed(e)
    return
  } finally {
    autoBusy.value = false
  }
  if (!suggestionCount.value) {
    useToast().add({ title: 'No new cuts found', description: 'Auto-slice didn’t find cut lines beyond what you already have.', color: 'neutral' })
  }
  render()
}
function acceptSuggestion(i: number) {
  const s = suggested.value[i]
  if (!s) return
  insertHCut(s.y)
  logSuggestionLabel(s, true)
  suggested.value.splice(i, 1)
  selected.value = { kind: 'h', idx: hCuts.value.length - 1 }
  render()
}
// A vertical suggestion goes into every band that lies mostly inside its rows. Vertical
// accepts aren't logged: slice_labels holds horizontal feature vectors only.
function acceptSuggestionV(s: VCutSuggestion) {
  for (const b of bands.value) {
    const overlap = Math.min(b.y1, s.y1) - Math.max(b.y0, s.y0)
    if (overlap <= (b.y1 - b.y0) / 2) continue
    const arr = (vCuts.value[b.index] ??= [])
    if (!arr.some((x) => Math.abs(x - s.x) < 20)) arr.push(s.x)
  }
  suggestedV.value = suggestedV.value.filter((o) => o !== s)
}
function acceptAllSuggestions() {
  const taken = shownSuggestions.value
  for (const s of taken) { insertHCut(s.y); logSuggestionLabel(s, true) }
  suggested.value = suggested.value.filter((s) => !taken.includes(s)) // the rest stay below the slider
  for (const s of shownSuggestionsV.value) acceptSuggestionV(s) // after the horizontals, so the bands exist
  render()
}
function dismissSuggestions() {
  for (const s of suggested.value) logSuggestionLabel(s, false)
  suggested.value = []
  suggestedV.value = []
  render()
}

// Data-collection loop: every accept/reject is a labelled training example for the ranker.
// Fire-and-forget; failures are non-critical (they only cost a future label).
function logSuggestionLabel(s: Suggestion, accepted: boolean) {
  $fetch('/api/v1/slicer/labels', {
    method: 'POST',
    body: {
      parent: groupAlias,
      alias: emailAlias,
      size: prefixFromName(),
      y: s.y,
      height: realHeight.value,
      features: s.features,
      accepted,
    },
  }).catch(() => { /* best-effort */ })
}

function resetCuts() {
  hCuts.value = []
  vCuts.value = []
  activeBand.value = 0
  selected.value = null
  suggested.value = []; suggestedV.value = []
  render()
}

async function saveCuts() {
  const payload = { hCuts: hCuts.value, vCuts: vCuts.value, links: links.value }
  // Cache locally for instant reloads, then persist to the email record so cuts
  // survive across machines/browsers (the record is the source of truth).
  try {
    localStorage.setItem(`slicer:${groupAlias}/${emailAlias}`, JSON.stringify(payload))
  } catch {
    // localStorage may be unavailable (private mode) — the server save below still runs.
  }
  try {
    await $fetch(`/api/v1/emails/${groupAlias}/${emailAlias}/cuts`, { method: 'POST', body: payload })
    useToast().add({ title: 'Cuts saved', description: 'Cut configuration saved to this email.', color: 'success' })
  } catch (error: any) {
    useToast().add({
      title: 'Saved locally only',
      description: error?.statusMessage || 'Could not save cuts to the server; kept a local copy.',
      color: 'warning',
    })
  }
}

async function loadCuts() {
  // Prefer the persisted record; fall back to the local cache if the request fails.
  try {
    const res = await $fetch<any>(`/api/v1/emails/${groupAlias}/${emailAlias}/cuts`)
    if (res?.cuts) {
      hCuts.value = res.cuts.hCuts || []
      vCuts.value = res.cuts.vCuts || []
      links.value = res.cuts.links || {}
      return
    }
  } catch {
    // fall through to local cache
  }
  try {
    const raw = localStorage.getItem(`slicer:${groupAlias}/${emailAlias}`)
    if (!raw) return
    const data = JSON.parse(raw)
    hCuts.value = data.hCuts || []
    vCuts.value = data.vCuts || []
    links.value = data.links || {}
  } catch {
    // ignore
  }
}

// ---- Keyboard ----
function handleKeydown(event: KeyboardEvent) {
  // Cuts must not change mid-export — the overlay blocks the mouse, this blocks the shortcuts.
  if (exporting.value) return
  const target = event.target as HTMLElement
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

  if (event.key === 'Delete' || event.key === 'Backspace') {
    deleteSelectedOrLast(); render(); event.preventDefault()
  } else if (event.key === 'ArrowUp') {
    nudgeSelected(0, -1); event.preventDefault()
  } else if (event.key === 'ArrowDown') {
    nudgeSelected(0, 1); event.preventDefault()
  } else if (event.key === 'ArrowLeft') {
    nudgeSelected(-1, 0); event.preventDefault()
  } else if (event.key === 'ArrowRight') {
    nudgeSelected(1, 0); event.preventDefault()
  } else if (event.key === 'Escape') {
    selected.value = null; suggested.value = []; suggestedV.value = []; render(); event.preventDefault()
  } else if (event.key === 'v' || event.key === 'V') {
    cutMode.value = cutMode.value === 'vertical' ? 'horizontal' : 'vertical'
    event.preventDefault()
  } else if (event.key === 'Shift') {
    flipHeld.value = true
    render()
  }
}

function handleKeyup(event: KeyboardEvent) {
  // Always clear on Shift release (even if focus is in a field) to avoid a stuck flip.
  if (event.key === 'Shift') {
    flipHeld.value = false
    render()
  }
}

// Nudge the SELECTED cut by (dx, dy). Falls back to the last cut of the current mode when
// nothing is selected (preserves the old arrow-key behaviour).
function nudgeSelected(dx: number, dy: number) {
  const sel = selected.value
  if (sel?.kind === 'h') {
    const v = hCuts.value[sel.idx]
    if (v !== undefined) moveHCut(sel.idx, v + dy)
  } else if (sel?.kind === 'v') {
    const arr = vCuts.value[sel.band]
    if (arr && arr[sel.idx] !== undefined) arr[sel.idx] = Math.max(1, Math.min(realWidth.value - 1, arr[sel.idx]! + dx))
  } else if (cutMode.value === 'horizontal') {
    const last = hCuts.value.at(-1); if (last !== undefined) moveHCut(hCuts.value.length - 1, last + dy)
  } else {
    const arr = vCuts.value[activeBand.value]; const last = arr?.pop(); if (last !== undefined) arr!.push(last + dx)
  }
  render()
}

function deleteSelectedOrLast() {
  const sel = selected.value
  if (sel?.kind === 'h') { removeHCut(sel.idx); selected.value = null }
  else if (sel?.kind === 'v') { vCuts.value[sel.band]?.splice(sel.idx, 1); selected.value = null }
  else if (cutMode.value === 'horizontal') removeHCut(hCuts.value.length - 1)
  else vCuts.value[activeBand.value]?.pop()
}

// Slice keys (number+letter) of cells that already carry alt text, read from the current
// email. Used to decide whether re-OCR would overwrite anything, and which cells to skip.
function existingAltKeys(): Set<string> {
  const out = new Set<string>()
  const target = email.value
  if (!target) return out
  const keyRegex = /(?:\$SIZE|desktop|mobile)_(\d+)(?:_?([a-z]))?\./i
  for (const s of normalizeSections(target.sections)) {
    if (s.type !== 'image') continue
    for (const c of s.cells || []) {
      const m = keyRegex.exec(c.src || '')
      if (m && c.alt) out.add(`${parseInt(m[1]!, 10)}${(m[2] || '').toLowerCase()}`)
    }
  }
  return out
}

// ---- Export ----
async function sliceAndExport() {
  if (!image.value || !bands.value.length) {
    useToast().add({ title: 'Nothing to slice', description: 'Add at least one cut first.', color: 'warning' })
    return
  }

  // If re-OCR would overwrite alt text that's already there, ask first — before the blocking
  // overlay goes up (the confirm dialog would otherwise render beneath it). "Keep" preserves
  // existing alt and only OCRs slices that have none.
  const existingAlt = existingAltKeys()
  let replaceAlt = true
  if (ocrEnabled.value && existingAlt.size > 0) {
    replaceAlt = await useConfirmDialog().confirm({
      title: 'Replace existing alt text?',
      message: `${existingAlt.size} slice${existingAlt.size === 1 ? '' : 's'} already ${existingAlt.size === 1 ? 'has' : 'have'} alt text. Replace it with fresh OCR, or keep it and only fill slices that have none?`,
      action: 'Replace with OCR',
    })
  }

  // Which cells will actually be OCR'd (for the progress count).
  const willOcr = (band: Band, j: number) =>
    ocrEnabled.value && (replaceAlt || !existingAlt.has(`${band.index + 1}${cellLetter(band, j)}`))

  exporting.value = true
  ocrStatus.value = ''
  // Alt text collected per cell (keyed like `links`: `${band.index}-${j}`).
  const altByCell: Record<string, string> = {}
  try {
    const prefix = prefixFromName()
    const formData = new FormData()
    let total = 0
    for (const band of bands.value) for (let j = 0; j < band.cells.length; j++) if (willOcr(band, j)) total++
    let done = 0

    for (const band of bands.value) {
      const num = String(band.index + 1).padStart(2, '0')
      for (let j = 0; j < band.cells.length; j++) {
        const cell = band.cells[j]!
        const w = Math.max(1, Math.round(cell.x1 - cell.x0))
        const h = Math.max(1, Math.round(band.y1 - band.y0))
        const sliceCanvas = document.createElement('canvas')
        sliceCanvas.width = w
        sliceCanvas.height = h
        const sliceCtx = sliceCanvas.getContext('2d')
        if (!sliceCtx) continue
        sliceCtx.drawImage(image.value, cell.x0, band.y0, w, h, 0, 0, w, h)

        // OCR the slice (before JPEG compression, for best legibility) to auto-fill alt text.
        // Skipped for cells whose alt the user chose to keep.
        if (willOcr(band, j)) {
          done++
          ocrStatus.value = `reading text… ${done}/${total}`
          const text = await ocrRecognize(sliceCanvas)
          if (text) altByCell[`${band.index}-${j}`] = text
        }

        const blob = await new Promise<Blob>((resolve) => sliceCanvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.85))
        formData.append('images', blob, `${prefix}_${num}${cellSuffix(band, j)}.jpg`)
      }
    }

    if (total) ocrStatus.value = 'uploading…'
    await $fetch(`/api/v1/images/${groupAlias}/${emailAlias}/upload`, { method: 'POST', body: formData })

    applySlicesToEmail(altByCell, replaceAlt)

    const altCount = Object.keys(altByCell).length
    useToast().add({
      title: `Sliced & exported (${prefix})`,
      description: `${totalCells.value} cell image${totalCells.value === 1 ? '' : 's'} uploaded${altCount ? ` · alt text on ${altCount}` : ''}.`,
      color: 'success',
    })

    // After slicing the desktop design, nudge the user to also slice the mobile design
    // (the responsive templates reference both desktop_* and mobile_* files via $SIZE).
    showMobilePrompt.value = prefix === 'desktop'
  } catch (error) {
    console.error('Slice export error:', error)
    useToast().add({ title: 'Error', description: 'Failed to export slices.', color: 'error' })
  } finally {
    exporting.value = false
    ocrStatus.value = ''
    // Free the OCR worker + WASM until the next export.
    if (ocrEnabled.value) ocrTerminate()
  }
}

// Rebuild the email's IMAGE sections from the current cuts (the slicer is the source of
// truth for image layout): one band per horizontal cut, split into cells where there are
// vertical cuts. Text/spacer sections are preserved; the image bands are consolidated at
// the position of the first existing image section (so re-slicing replaces rather than
// duplicates — e.g. splitting band 01 removes the old single `01`).
function applySlicesToEmail(altByCell: Record<string, string> = {}, replaceExistingAlt = true) {
  const target = email.value
  if (!target) return

  // Separate mobile layout: `sections` = desktop, `mobileSections` = mobile. When on, write
  // literal desktop_*/mobile_* srcs into the matching tree (each tree is already size-scoped,
  // so no $SIZE swap). When off, keep the single shared tree with $SIZE.
  const separate = Array.isArray(target.mobileSections)
  const prefix = prefixFromName() // 'desktop' | 'mobile'
  const useMobileTree = separate && prefix === 'mobile'
  const sizePrefix = separate ? prefix : '$SIZE'

  const tree = normalizeSections(useMobileTree ? target.mobileSections! : target.sections)

  // Carry forward links + existing alt text from cells that already exist (matched by slice key).
  // Tolerant of both the old glued ("04a") and new underscore ("04_a") cell-letter formats.
  const keyRegex = /(?:\$SIZE|desktop|mobile)_(\d+)(?:_?([a-z]))?\./i
  const existingLinkByKey = new Map<string, string>()
  const existingAltByKey = new Map<string, string>()
  for (const s of tree) {
    if (s.type === 'image') {
      for (const c of s.cells || []) {
        const m = keyRegex.exec(c.src || '')
        if (m) {
          const k = `${parseInt(m[1]!, 10)}${(m[2] || '').toLowerCase()}`
          if (c.link) existingLinkByKey.set(k, c.link)
          if (c.alt) existingAltByKey.set(k, c.alt)
        }
      }
    }
  }

  const imageBands: EmailSectionT[] = bands.value.map((band) => {
    const num = String(band.index + 1).padStart(2, '0')
    const isSplit = band.cells.length > 1
    const cells: EmailImageT[] = band.cells.map((cell, j) => {
      const letter = cellLetter(band, j)
      const key = `${band.index + 1}${letter}`
      const cellKey = `${band.index}-${j}`
      const link = links.value[cellKey] || existingLinkByKey.get(key) || undefined
      // Fresh OCR normally wins; when the user chose to keep existing alt, it takes precedence
      // (fresh OCR only fills cells that had none — those aren't in existingAltByKey).
      const alt = replaceExistingAlt
        ? (altByCell[cellKey] || existingAltByKey.get(key) || undefined)
        : (existingAltByKey.get(key) || altByCell[cellKey] || undefined)
      // Store the RAW cut width as a proportion weight; the builder derives exact
      // percentages from these so cells stay perfectly proportional (uniform height).
      const width = isSplit ? Math.round(cell.x1 - cell.x0) : undefined
      const c: EmailImageT = { src: `${sizePrefix}_${num}${cellSuffix(band, j)}.jpg` }
      if (link) c.link = link
      if (alt) c.alt = alt
      if (width !== undefined) c.width = width
      return c
    })
    return { type: 'image', cells }
  })

  // The portal seeds a new draft with a single raw-HTML "scaffold" section that embeds the
  // full design comp + copy so there's something to look at before slicing (see
  // server/api/external/emails.post.ts). Once sliced, the bands ARE the content, so the
  // scaffold is a placeholder to replace — identified by its marker comment.
  const isScaffold = (s: EmailSectionT) =>
    s.type === 'html' && /draft scaffold from portal request/.test(s.html || '')

  // Preserve genuine non-image sections; drop existing image sections and the portal
  // scaffold; splice the rebuilt bands in at the position of whichever came first.
  const result: EmailSectionT[] = []
  let inserted = false
  for (const s of tree) {
    if (s.type === 'image' || isScaffold(s)) {
      if (!inserted) {
        result.push(...imageBands)
        inserted = true
      }
    } else {
      result.push(s)
    }
  }
  if (!inserted) result.push(...imageBands)

  // Write back to the tree we sliced into (the other tree is left untouched).
  if (useMobileTree) target.mobileSections = result
  else target.sections = result

  // Persist both trees (mobileSections only when separate mode is on).
  const body: Record<string, unknown> = { ...target, sections: serializeSections(target.sections) }
  if (Array.isArray(target.mobileSections)) body.mobileSections = serializeSections(target.mobileSections)
  $fetch(`/api/v1/emails/${groupAlias}/${emailAlias}`, { method: 'PUT', body })
    .catch((e) => console.error('Failed to save sliced sections:', e))
}

onMounted(() => {
  loadCuts()
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('keyup', handleKeyup)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('keyup', handleKeyup)
  subbarStatus.value = null
  releaseObjectUrl()
})
</script>

<style scoped>
.slicer {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.slicer-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

/* Three columns that wrap to a stack on narrow screens. */
.slicer-cols {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 20px;
}

.slicer-side {
  display: flex;
  flex: 1 1 250px;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

@media (min-width: 1100px) {
  .slicer-side {
    position: sticky;
    top: 74px;
    max-width: 320px;
    max-height: calc(100vh - 90px);
    overflow: auto;
  }
}

.slicer-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--panel);
}

.slicer-h {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--muted);
}

.asset-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--field);
  color: var(--muted);
  cursor: pointer;
}

.asset-btn.on {
  border-color: var(--acctext);
  background: var(--raise);
  color: var(--ink);
}

.asset-btn:disabled {
  cursor: progress;
}

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  border: 1px dashed var(--line2);
  border-radius: 12px;
  background: var(--field);
  color: var(--muted);
  font-size: 13px;
  text-align: center;
  cursor: pointer;
}

.dropzone:hover {
  color: var(--ink);
}

.slicer-keys {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 16px;
  color: var(--muted);
  font-size: 13px;
}

.slicer-keys kbd {
  display: inline-block;
  min-width: 22px;
  margin-right: 4px;
  padding: 0 6px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--raise);
  font: inherit;
  font-size: 12px;
  text-align: center;
}

.slicer-canvas {
  display: flex;
  flex: 999 1 420px;
  justify-content: center;
  min-width: 0;
  padding: 24px 16px 32px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--well);
  overflow-x: auto;
}

.slicer-stage {
  display: flex;
  width: 560px;
  max-width: 100%;
}

.slicer-gutter {
  position: relative;
  flex: 0 0 76px;
}

.gutter-label {
  position: absolute;
  right: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--muted);
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  cursor: pointer;
}

.gutter-label b {
  color: var(--ink);
}

.gutter-label.on,
.gutter-label.on b {
  color: var(--acctext);
}

.band-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
  overflow: hidden;
  cursor: pointer;
}

.band-card.on {
  border-color: var(--acctext);
}

.band-head {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 40px;
  padding: 0 14px;
  border: 0;
  background: none;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}

.band-card.on .band-head {
  background: var(--raise);
}

.band-card.on .band-head b {
  color: var(--acctext);
}

.band-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-top: 1px solid var(--line);
}

/* Full-viewport blocking overlay shown while exporting. */
.slicer-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--bg) 72%, transparent);
  backdrop-filter: blur(3px);
  cursor: wait;
}

.slicer-overlay__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: 320px;
  max-width: calc(100vw - 40px);
  padding: 26px 24px;
  background: var(--panel);
  border: 1px solid var(--line2);
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
}

.slicer-spinner {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid var(--line2);
  border-top-color: var(--accent);
  animation: slicer-spin 0.7s linear infinite;
}

.slicer-overlay__status {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  text-align: center;
}

.slicer-overlay__bar {
  width: 100%;
  height: 5px;
  border-radius: 3px;
  background: var(--panel2);
  overflow: hidden;
}

.slicer-overlay__bar-fill {
  height: 100%;
  background: var(--accent);
  border-radius: 3px;
  transition: width 0.2s ease;
}

/* Indeterminate sweep for phases without an X/Y count (e.g. uploading). */
.slicer-overlay__bar-fill.is-indeterminate {
  width: 40%;
  animation: slicer-sweep 1.1s ease-in-out infinite;
}

.slicer-overlay__hint {
  font-size: 13px;
  color: var(--muted);
  text-align: center;
}

@keyframes slicer-spin {
  to { transform: rotate(360deg); }
}

@keyframes slicer-sweep {
  0% { transform: translateX(-110%); }
  100% { transform: translateX(275%); }
}
</style>
